import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import { apiGetFormSchema, apiListTeacherTasks } from '../../utils/rmsApi';
import {
  countSchemaQuestions, estimatedMinutes, harbourFormRoute, harbourFormTitle,
} from '../../utils/harbourForms';
import { HFP_V4_PORTAL_FORM_KEY } from '../../utils/referralStatus';
import {
  PATHWAY_LABELS, sencoNote, teacherFormRows, teacherPupilSummary,
} from '../../utils/teacherPupils';
import HarbourTopBar from '../../components/harbour/HarbourTopBar';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourProgressSegments from '../../components/harbour/HarbourProgressSegments';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import '../../components/harbour/harbour.css';

/*
 * One pupil's forms for a teacher (TDI-620, design 3579:5294, phone 3606:10828):
 * the pathway label, the pupil's name and year group, "{x} of {y} forms
 * done", the time it takes, the teacher's forms only (one at a time, as on
 * the school forms homepage), the SENCO's note and "How this works", and a
 * "Your pupils" link back. "Download your answers" prints the forms they
 * have finished (T-8).
 *
 * A teacher's own submit locks nothing; only the SENCO's confirm does
 * (TDI-618, IN-13), so the finished state says the answers can still change
 * until then, and "locked" only once the server says they are.
 *
 * Everything comes from rmsListTeacherTasks and the public form schemas — a
 * teacher has no client reads of the record (TDI-489, unchanged).
 */
export default function TeacherPupilPage() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const { user, claims } = useAuth();
  const orgId = claims?.rmsOrgId || getOrgConfig().orgId;

  const [state, setState] = useState({ loading: true, error: null, tasks: null });
  const [schemas, setSchemas] = useState({});

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const tasks = await apiListTeacherTasks({ orgId });
      setState({ loading: false, error: null, tasks });
    } catch (e) {
      setState({ loading: false, error: e?.message || 'Your forms could not be loaded. Check your connection and try again.', tasks: null });
    }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const tasks = state.tasks;
  const child = useMemo(
    () => ((tasks && tasks.children) || []).find((c) => c.patientRecordId === patientRecordId) || null,
    [tasks, patientRecordId],
  );
  // The statement comes first, once, on the list page.
  useEffect(() => {
    if (tasks && !tasks.statementAccepted) navigate('/rms/teacher', { replace: true });
  }, [tasks, navigate]);

  const formKeysKey = child ? child.forms.map((f) => f.formKey).join('|') : '';
  useEffect(() => {
    if (!formKeysKey) return undefined;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(formKeysKey.split('|').map(async (formKey) => {
        if (formKey === HFP_V4_PORTAL_FORM_KEY) return [formKey, null];
        try {
          const res = await apiGetFormSchema({ orgId, formKey });
          return [formKey, (res && res.schema) || null];
        } catch (_) {
          return [formKey, null];
        }
      }));
      if (!cancelled) setSchemas(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [formKeysKey, orgId]);

  const userName = [tasks?.firstName].filter(Boolean).join(' ') || undefined;
  const topBar = (
    <HarbourTopBar
      navItems={[]}
      userName={userName}
      // The same account menu as "Your pupils" (TDI-702).
      accountEmail={user?.email}
      showChangePassword={claims?.authMode === 'password'}
      onSignOut={async () => {
        try { await auth.signOut(); } catch (_) { /* no-op */ }
        navigate('/rms/school-sign-in?signedout=1');
      }}
    />
  );
  const back = (
    <span>
      <HarbourTextLink to="/rms/teacher" leadingIcon="arrow-left" size="standard">Your pupils</HarbourTextLink>
    </span>
  );
  const shell = (content) => (
    <div className="harbour harbour-page">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 xl:px-10 py-5 flex flex-col gap-6 text-left">
        {topBar}
        {back}
        {content}
      </div>
    </div>
  );

  if (state.loading) {
    return shell(
      <div aria-busy="true" className="flex flex-col gap-4">
        <p className="harbour-body-16">Loading your forms…</p>
        <div className="harbour-skeleton h-[96px] animate-pulse" aria-hidden="true" />
        <div className="harbour-skeleton h-[72px] animate-pulse" aria-hidden="true" />
      </div>,
    );
  }
  if (state.error) {
    return shell(
      <HarbourNotice kind="error" title="That did not work">
        {state.error}
        {' '}
        <HarbourTextLink onClick={load}>Try again</HarbourTextLink>
      </HarbourNotice>,
    );
  }
  if (!child) {
    return shell(
      <div className="harbour-form-card text-left flex flex-col gap-3">
        <h1 className="harbour-title-20">This pupil is not on your list</h1>
        <p className="harbour-body-16">
          Your SENCO may have given their forms to someone else, or the link you followed may be out of date.
        </p>
      </div>,
    );
  }

  const summary = teacherPupilSummary(child);
  const rows = teacherFormRows(child.forms);
  const tone = summary.pathways.includes('sen') && !summary.pathways.includes('referral') ? 'sen' : 'referral';
  const locked = child.forms.length > 0 && child.forms.every((f) => f.locked === true);
  const firstName = child.pupilFirstName || String(child.pupilName || '').split(' ')[0];

  const meta = (formKey) => {
    const schema = schemas[formKey];
    const count = countSchemaQuestions(schema, 'school_user');
    const minutes = estimatedMinutes(schema, count);
    return {
      count,
      minutes,
      line: [
        count ? `${count} question${count === 1 ? '' : 's'}` : null,
        minutes ? `about ${minutes} minute${minutes === 1 ? '' : 's'}` : null,
      ].filter(Boolean).join(' · '),
    };
  };
  const allMinutes = child.forms.map((f) => meta(f.formKey).minutes);
  const totalMinutes = allMinutes.every(Boolean) ? allMinutes.reduce((a, b) => a + b, 0) : null;
  const firstOpenToDo = rows.find((r) => r.open && r.rowStatus !== 'done');
  const openForm = (row) => {
    const route = harbourFormRoute(child.patientRecordId, row.formKey);
    if (!route) return;
    navigate(row.rowStatus === 'done' ? `${route}?mode=review` : route, {
      state: { pupilFirstName: firstName || null, locked: row.locked === true, pathway: row.pathway || 'referral' },
    });
  };
  const download = () => window.open(
    `/rms/teacher/pupils/${encodeURIComponent(child.patientRecordId)}/answers/print`, '_blank', 'noopener',
  );

  return shell(
    <>
      <div className="flex flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className={`harbour-flyout__eyebrow harbour-flyout__eyebrow--${tone}`}>
            <HarbourPathwayMark pathway={tone} />
            School forms
          </span>
          {summary.pathways.map((p) => (
            <span key={p} className={`harbour-flyout__eyebrow harbour-flyout__eyebrow--${p}`}>
              <HarbourPathwayMark pathway={p} />
              {PATHWAY_LABELS[p]}
            </span>
          ))}
        </span>
        <h1 className="flex flex-wrap items-baseline gap-x-3">
          <span className="harbour-heading-32">{child.pupilName}</span>
          {child.yearGroup && <span className="harbour-flyout__year">{child.yearGroup}</span>}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_344px] gap-6 items-start">
        <div className="harbour-form-card flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-4">
            {summary.allDone ? (
              <span className="flex items-center gap-3">
                <span className="harbour-forms-home__done-disc" aria-hidden="true">
                  <HarbourIcon name="check" size={16} />
                </span>
                <h2 className="harbour-form-card__title">{`All ${summary.total} forms submitted`}</h2>
              </span>
            ) : (
              <h2 className="harbour-form-card__title">{`${summary.done} of ${summary.total} forms done`}</h2>
            )}
            {summary.done > 0 && (
              <span className="ml-auto flex-none">
                <HarbourButton kind="secondary" leadingIcon="download" onClick={download}>
                  Download your answers
                </HarbourButton>
              </span>
            )}
          </div>
          {summary.allDone ? (
            <p className="harbour-body-16">
              {locked
                ? 'You’ve submitted your answers, so they’re now locked and can’t be changed. You can still read them and download a copy.'
                : 'Change any answer on the review page until your SENCO submits the school’s forms. After that they are locked.'}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="harbour-body-16">
                {totalMinutes
                  ? `About ${totalMinutes} minutes in total. Your answers save as you go.`
                  : 'Your answers save as you go.'}
              </p>
              <HarbourProgressSegments tone={tone} total={summary.total} doneCount={summary.done} />
            </div>
          )}
          <div className="flex flex-col gap-2">
            {rows.map((row, i) => {
              const title = harbourFormTitle(row.formKey, schemas[row.formKey]);
              const done = row.rowStatus === 'done';
              const rowTone = row.pathway === 'sen' ? 'sen' : 'referral';
              return (
                <div
                  key={row.formKey}
                  className={[
                    'harbour-form-row',
                    `harbour-form-row--${rowTone}`,
                    done ? 'harbour-form-row--done' : '',
                    row.open && !done ? 'harbour-form-row--open' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <span className="harbour-form-row__disc" aria-hidden="true">
                    {done ? <HarbourIcon name="check" size={16} /> : i + 1}
                  </span>
                  <span className="harbour-form-row__text">
                    <span className="harbour-body-16-bold">{title}</span>
                    <span className="harbour-form-row__meta">
                      {done ? 'All questions completed' : meta(row.formKey).line}
                    </span>
                  </span>
                  <span className="ml-auto flex items-center gap-3 flex-none flex-wrap justify-end">
                    {row.open ? (
                      <>
                        <HarbourStatusChip
                          status={row.rowStatus}
                          label={done ? 'Submitted' : undefined}
                          tone={done ? rowTone : 'neutral'}
                          surface={done ? 'onWhite' : 'tinted'}
                        />
                        {done ? (
                          <HarbourTextLink onClick={() => openForm(row)}>
                            {row.locked ? 'View' : 'Review your answers'}
                          </HarbourTextLink>
                        ) : (
                          <HarbourButton
                            kind={row === firstOpenToDo ? 'primary' : 'secondary'}
                            trailingIcon="arrow-right"
                            aria-label={`${row.rowStatus === 'inProgress' ? 'Continue' : 'Start'} ${title}`}
                            onClick={() => openForm(row)}
                          >
                            {row.rowStatus === 'inProgress' ? 'Continue' : 'Start'}
                          </HarbourButton>
                        )}
                      </>
                    ) : (
                      <span className="harbour-form-row__locked">
                        <HarbourIcon name="lock" size={16} />
                        Locked
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <aside className="flex flex-col gap-4">
          <HarbourNotice kind="info">
            {sencoNote({ sencoName: child.sencoName, schoolName: child.schoolName, pupilName: child.pupilName })}
          </HarbourNotice>
          {!summary.allDone && (
            <HarbourNotice kind="info" title="How this works">
              Answer one form at a time. The next one opens when you finish the one before. Finished forms stay open.
            </HarbourNotice>
          )}
        </aside>
      </div>
    </>,
  );
}
